'use client';

import Image from 'next/image';
import ShimmerImage from '@/components/ui/ShimmerImage';
import Link from 'next/link';
import { MapPin } from 'lucide-react';

type HomeBannerProps = {
  onBannerJobClick?: (id: number) => void;
  bannerId?: number;
  companyId?: number;
  companyLogo?: string;
  companyName?: string;
  showJobCount?: boolean;
  jobCount?: number;
  jobTitle?: string;
  salaryRange?: string;
  location?: string;
  isSponsored?: boolean;
  companyLink?: string;
  jobLink?: string;
  address?: string;
};

export default function HomeBanner({
  onBannerJobClick,
  bannerId,
  companyId = 0,
  companyLogo = '',
  companyName = '',
  showJobCount = false,
  jobCount = 0,
  jobTitle = '',
  salaryRange = '',
  location = '',
  isSponsored = true,
  companyLink = '',
  jobLink = '',
  address = '',
}: HomeBannerProps) {
  return (
    <div
      className="relative h-[112px] border-t border-black/15 shadow-md hover:bg-gray-100 md:h-[136px]"
      onClick={() => {
        sessionStorage?.setItem('jobs-scroll', window.scrollY.toString());
      }}
    >
      <Link
        className="flex h-full w-full flex-row items-stretch gap-2 px-4 pb-4 pt-5 md:gap-4 md:px-9 md:pb-5 md:pt-7"
        onClick={() => {
          if (bannerId) {
            onBannerJobClick?.(bannerId);
          }
        }}
        href={showJobCount ? `/company/${companyLink}` : `/job/${jobLink}`}
      >
        <div className="flex w-1/2 items-center gap-2 md:w-auto md:gap-4">
          <ShimmerImage
            src={companyLogo}
            alt={companyName}
            width={1920}
            height={1080}
            className="h-auto w-[50px] rounded-lg md:w-[85px]"
            objectType="company"
            objectID={companyId}
          />
          <div className="flex max-w-auto flex-col md:max-w-[160px] md:min-w-[160px]">
            <span className="line-clamp-3 break-words text-sm font-normal md:text-base">
              {companyName}
            </span>
            <span className="flex items-center gap-1 text-[10px] font-normal text-[#696B6D] md:text-xs">
              <MapPin size={12} className="shrink-0" />
              {address}
            </span>
          </div>
        </div>

        <div className="hidden h-auto w-px bg-black/20 md:block"></div>

        {showJobCount ? (
          <div className="flex w-1/2 flex-row items-center gap-2 md:w-auto md:gap-4">
            <div className="w-[62px] text-center text-[40px] font-bold leading-5 text-[#05427B] md:w-auto md:min-w-[98px] md:text-7xl">
              {jobCount}
            </div>
            <div className="flex flex-col text-sm font-medium text-[#696B6D] md:text-2xl md:leading-7">
              <span>НЭЭЛТТЭЙ</span>
              <span>АЖЛЫН БАЙР</span>
            </div>
          </div>
        ) : (
          <div className="flex w-1/2 flex-col justify-center text-sm md:w-auto">
            <span className="mb-1.5 max-w-[370px] truncate font-bold uppercase text-[#05427B] md:text-black">
              {jobTitle}
            </span>
            <span>{salaryRange}</span>
            <span className="flex flex-row gap-2">
              {location}{' '}
              <Image
                src="/icons/icon-bannerZar.svg"
                alt="bannerZar"
                width={13}
                height={13}
              />
            </span>
          </div>
        )}
      </Link>
      {isSponsored && (
        <div className="absolute right-0 top-0 flex h-[19px] w-[81px] items-center justify-center bg-secondary text-[10px] text-white">
          <span className="font-bold">sponsored</span>
        </div>
      )}
    </div>
  );
}
